import * as React from 'react';
import { observableValue } from '@vscode/observables';
import { view, viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import { styles, mergeStyles } from './styles.js';
import { ChevronRightIcon, ChevronDownIcon, ComponentIcon, FolderIcon } from './icons.js';
import type { ExplorerModel } from './ExplorerModel.js';
import type { ExplorerTreeNode } from './types.js';

const labelContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '3px',
  marginLeft: '6px',
  overflow: 'hidden',
  flexShrink: 1,
  minWidth: 0,
};

function countFixtures(node: ExplorerTreeNode): number {
  if (node.type === 'component') { return 1; }
  return (node.children ?? []).reduce((sum, child) => sum + countFixtures(child), 0);
}

const labelBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  lineHeight: '14px',
  padding: '0 4px',
  borderRadius: '2px',
  backgroundColor: 'var(--component-explorer-badge-background)',
  color: 'var(--component-explorer-badge-foreground)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '60px',
  cursor: 'pointer',
};

// TreeView - renders a list of nodes
export const TreeView = view(
  {
    model: prop.const<ExplorerModel>(),
    nodes: prop.const<readonly ExplorerTreeNode[]>(),
    depth: prop.const<number>(),
  },
  (_reader, props) => (
    <>
      {props.nodes.map((node) => (
        <TreeItem key={node.id.value} model={props.model} node={node} depth={props.depth} />
      ))}
    </>
  )
);

// TreeItem - ViewModel for hover state
class TreeItemModel extends ViewModel({
  model: prop.const<ExplorerModel>(),
  node: prop.const<ExplorerTreeNode>(),
  depth: prop.const<number>(),
}) {
  readonly isHovered = observableValue<boolean>(this, false);

  // Expose props for render function
  get explorerModel(): ExplorerModel { return this.props.model; }
  get treeNode(): ExplorerTreeNode { return this.props.node; }
  get treeDepth(): number { return this.props.depth; }

  setHovered = (hovered: boolean): void => {
    this.isHovered.set(hovered, undefined);
  };

  handleClick = (): void => {
    this.props.model.selectNode(this.props.node.id.value);
  };

  handleDoubleClick = (): void => {
    if (this._hasChildren) {
      this.props.model.toggleNodeExpanded(this.props.node.id.value);
    }
  };

  handleChevronClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    this.props.model.toggleNodeExpanded(this.props.node.id.value);
  };

  private get _hasChildren(): boolean {
    return !!(this.props.node.children && this.props.node.children.length > 0);
  }
}

export const TreeItem = viewWithModel(TreeItemModel, {}, (reader, itemModel) => {
  const model = itemModel.explorerModel;
  const node = itemModel.treeNode;
  const depth = itemModel.treeDepth;
  const hasChildren = !!(node.children && node.children.length > 0);
  const isExpanded = hasChildren ? model.isNodeExpanded(node.id.value, reader) : false;
  const selectedId = model.selectedNodeId.read(reader);
  const isSelected = selectedId === node.id.value;
  const isHovered = itemModel.isHovered.read(reader);

  const itemStyle = mergeStyles(
    styles.treeItem,
    { paddingLeft: `${8 + depth * 12}px` },
    isSelected && styles.treeItemSelected,
    !isSelected && isHovered && styles.treeItemHover,
  );

  return (
    <>
      <div
        style={itemStyle}
        onClick={itemModel.handleClick}
        onDoubleClick={itemModel.handleDoubleClick}
        onMouseEnter={() => itemModel.setHovered(true)}
        onMouseLeave={() => itemModel.setHovered(false)}
      >
        {hasChildren ? (
          <div style={styles.treeItemChevron} onClick={itemModel.handleChevronClick}>
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </div>
        ) : (
          <div style={styles.treeItemChevronPlaceholder} />
        )}
        <div style={styles.treeItemIcon}>
          {node.type === 'folder' 
            ? <FolderIcon style={{ color: 'var(--component-explorer-symbolIcon-folderForeground)' }} />
            : <ComponentIcon style={{ color: '#deb45b' }} />
          }
        </div>
        <span style={styles.treeItemLabel}>{node.name}</span>
        {node.labels.some(l => !l.startsWith('.')) && (
          <span style={labelContainerStyle}>
            {node.labels.filter(l => !l.startsWith('.')).map((label) => (
              <span
                key={label}
                style={labelBadgeStyle}
                onClick={(e) => { e.stopPropagation(); model.searchQuery.set(label, undefined); }}
                title={label}
              >
                {label}
              </span>
            ))}
          </span>
        )}
        {node.type === 'folder' && hasChildren && (
          <span style={styles.treeItemCount}>{countFixtures(node)}</span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <TreeView model={model} nodes={node.children!} depth={depth + 1} />
      )}
    </>
  );
});
